import { FC, useMemo } from 'react'
import { Paper } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { SensorQualityControlRow, SensorQualityControlRowChecks } from 'src/api'
import { TableToolbar } from 'src/components/ui'
import { EMPTY_VALUE, formatBatteryLevel, formatChecksPassed, formatQualityControlStatus, getQualityControlSeverity } from './qualityControlPresentation'

type CheckKey = keyof SensorQualityControlRowChecks

interface CheckColumnOptions {
  field: CheckKey
  headerName: string
  /** Battery shows its numeric level instead of the status name. */
  formatValue?: (row: SensorQualityControlRow) => string
}

const createCheckColumn = ({ field, headerName, formatValue }: CheckColumnOptions): GridColDef<SensorQualityControlRow> => ({
  field,
  headerName,
  flex: 1,
  minWidth: 130,
  valueGetter: (params) => (formatValue ? formatValue(params.row) : formatQualityControlStatus(params.row.checks?.[field]?.status)),
  cellClassName: (params) => {
    const severity = getQualityControlSeverity(params.row.checks?.[field]?.status)
    return severity === 'none' ? '' : `qualityControlCell--${severity}`
  }
})

const imeiColumn: GridColDef<SensorQualityControlRow> = {
  field: 'imei',
  headerName: 'IMEI',
  flex: 1,
  minWidth: 150,
  valueGetter: (params) => params.row.imei || params.row.uniqueId || EMPTY_VALUE
}

const checksPassedColumn: GridColDef<SensorQualityControlRow> = {
  field: 'passedChecks',
  headerName: 'Checks passed',
  flex: 1,
  minWidth: 175,
  valueGetter: (params) => formatChecksPassed(params.row.passedChecks, params.row.totalChecks)
}

const checkColumns: GridColDef<SensorQualityControlRow>[] = [
  createCheckColumn({ field: 'activation', headerName: 'Activation' }),
  createCheckColumn({ field: 'configuration', headerName: 'Configuration' }),
  createCheckColumn({ field: 'measurements', headerName: 'Measurements' }),
  createCheckColumn({ field: 'battery', headerName: 'Battery', formatValue: (row) => formatBatteryLevel(row.checks?.battery?.value) }),
  createCheckColumn({ field: 'signalStrength', headerName: 'Signal strength' })
]

interface SensorQualityControlTableProps {
  rows: SensorQualityControlRow[]
  isLoading?: boolean
  /** The multi-sensor view adds an IMEI column - the single-sensor section on the details page does not. */
  showImei?: boolean
  /** Grows with the content instead of filling a fixed-height container. Used for the single-row view. */
  autoHeight?: boolean
  exportedFileName?: string
}

export const SensorQualityControlTable: FC<SensorQualityControlTableProps> = ({ rows, isLoading = false, showImei = false, autoHeight = false, exportedFileName }) => {
  const columns = useMemo(() => (showImei ? [imeiColumn, ...checkColumns, checksPassedColumn] : [...checkColumns, checksPassedColumn]), [showImei])

  return (
    <Paper sx={autoHeight ? { width: '100%' } : { width: '100%', height: '100%', minHeight: '200px', maxHeight: '500px' }}>
      <DataGridPremium<SensorQualityControlRow>
        rows={rows}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.uniqueId as string}
        autoHeight={autoHeight}
        pagination={false}
        hideFooterPagination
        disableRowSelectionOnClick
        rowThreshold={2}
        columnThreshold={2}
        pinnedColumns={showImei ? { left: ['imei'] } : undefined}
        localeText={{ columnMenuSortAsc: 'Sort A-Z', columnMenuSortDesc: 'Sort Z-A', noRowsLabel: 'No sensors were checked' }}
        slots={exportedFileName ? { toolbar: () => <TableToolbar exportedFileName={exportedFileName} /> } : undefined}
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 1,
            fontSize: '13px',
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.6)',
            overflow: 'visible'
          },
          '& .qualityControlCell--error': { backgroundColor: 'rgba(240, 72, 72, 0.18)' },
          '& .qualityControlCell--warning': { backgroundColor: 'rgba(255, 193, 7, 0.28)' }
        }}
      />
    </Paper>
  )
}
